import type { DocumentMapping, DynamicDocumentInstructions } from "@/configuration/document-mappings/types";
import { type AutoIntakeConfig } from "@/src/lib/auto-intake/auto-intake-config";
import { getMappings, renderTemplate } from "@/src/lib/templating";
import { Classification, ClassificationSection } from "@/src/models/constants/classifications";
import { DefaultWorkflow, Workflow, WorkflowType } from "@/src/models/constants/group";
import { PriorAuthorizationStatus } from "@/src/models/documents/prior-authorization/prior-authorization";
import { Group } from "@/src/models/group";
import type { Section } from "@/src/models/section";

export const SIENT_AUTO_INTAKE_CONFIG: AutoIntakeConfig = {
  byClassification: {
    [Classification.REFERRAL]: { rate: 0.75 },
    [Classification.LAB_RESULTS]: { rate: 0.5 },
    [Classification.LAB_RESULTS_PATHOLOGY]: { rate: 0.5 },
    [Classification.PROGRESS_NOTE]: { rate: 0.5 },
    [Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY]: { rate: 0.5 },
    [Classification.PROGRESS_NOTE_HOSPITAL_RECORDS]: { rate: 0.5 },
    [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS_BARIUM_SWALLOW]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS_EKG]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS_ENG]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS_ESOPHAGRAM]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS_MRI]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS_CARDIAC]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS_ULTRASOUND]: { rate: 0.5 },
    [Classification.IMAGING_RESULTS_X_RAY]: { rate: 0.5 },
    [Classification.PRIOR_AUTHORIZATION]: { rate: 0.5 },
    [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: { rate: 0.5 },
    [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: { rate: 0.5 },
    [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: { rate: 0.5 },
    [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: { rate: 0.5 },
    [Classification.ADMIN]: { rate: 0.5 },
    [Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY]: { rate: 0.5 },
    [Classification.ADMIN_FMLA_FORMS]: { rate: 0.5 },
    [Classification.ADMIN_RETURN_TO_WORK]: { rate: 0.5 },
    [Classification.ADMIN_SCHOOL_NOTES]: { rate: 0.5 },
  },
};

/**
 * ORDERING PROVIDER - Need actual provider name and do a search
 * REQUESTING PROVIDER - Need actual provider name and do a search
 * HENRIKSON, CANDACE
 * STAFF, FMLA
 * STAFF, FRONT OFFICE
 * STAFF, MEDICAL RECORDS
 * STAFF, REFERRALS
 */
export const CANDACE_HENRIKSON_ID = "knw7q8sda7UihgWz222944y73wJt2lAzG9j..N9v6vA";
export const FMLA_STAFF_ID = "knw7q8sda7UihgWz22294-X3CPUP7UFuxYfQjveCTx8";
export const FRONT_OFFICE_STAFF_ID = "knw7q8sda7UihgWz222944DpQaBI3Jvhci9jG0sHnmw";
export const MEDICAL_RECORDS_STAFF_ID = "knw7q8sda7UihgWz222944-3OkPKok4Kd9.GVDKS93I";
export const REFERRALS_STAFF_ID = "knw7q8sda7UihgWz22294-Cig99CI3ZSVwPzH066RM0";
export const SIENT_REFERRAL_ASSIGNEE = "STAFF,REFERRALS";
export const VAL_AHMADI_HELA_ID = "knw7q8sda7UihgWz222941SrxlG0Ud2hexlH0-By63E";

export const SIENT_DOCUMENT_UPLOAD_PRACTITIONER_ID = VAL_AHMADI_HELA_ID;

/** Final fallback assignee for receiving-provider docs when no provider tier resolves. */
export const SIENT_FINAL_FALLBACK_STAFF_FAMILY_NAME = "Hollenkamp";
export const SIENT_FINAL_FALLBACK_STAFF_GIVEN_NAME = "Nicholas";

const SIENT_CLASSIFICATIONS_FOR_DISPLAY = [
  Classification.ADMIN,
  Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY,
  Classification.ADMIN_FMLA_FORMS,
  Classification.ADMIN_RETURN_TO_WORK,
  Classification.ADMIN_SCHOOL_NOTES,
  Classification.BLANK,
  Classification.CARE_PLAN,
  Classification.CLEARANCE_REQUEST,
  Classification.COVER_PAGE,
  Classification.DME_REQUEST,
  Classification.FINANCIAL,
  Classification.IDENTIFICATION_CARD,
  Classification.IMAGING_RESULTS,
  Classification.IMAGING_RESULTS_BARIUM_SWALLOW,
  Classification.IMAGING_RESULTS_EKG,
  Classification.IMAGING_RESULTS_ENG,
  Classification.IMAGING_RESULTS_ESOPHAGRAM,
  Classification.IMAGING_RESULTS_MRI,
  Classification.IMAGING_RESULTS_CARDIAC,
  Classification.IMAGING_RESULTS_ULTRASOUND,
  Classification.IMAGING_RESULTS_X_RAY,
  Classification.INCOMPLETE,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.JUNK,
  Classification.LAB_RESULTS,
  Classification.LAB_RESULTS_PATHOLOGY,
  Classification.LEGAL,
  Classification.LEGAL_POWER_OF_ATTORNEY,
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.NOTIFICATION_APPOINTMENT_CANCELLED_MISSED,
  Classification.NOTIFICATION_APPOINTMENT,
  Classification.OTHER,
  Classification.PHARMACY_RELATED_REFILL,
  Classification.PRIOR_AUTHORIZATION,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
  Classification.PROCEDURE_REHAB,
  Classification.PROCEDURE_SLEEP,
  Classification.PROGRESS_NOTE,
  Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
  Classification.PROGRESS_NOTE_HOSPITAL_RECORDS,
  Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
  Classification.REFERRAL,
  Classification.REFERRAL_REQUEST,
  Classification.UNKNOWN,
] as Classification[];

export function getSientClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [
    {
      label: "Classification",
      options: SIENT_CLASSIFICATIONS_FOR_DISPLAY,
    },
  ];
}

const SIENT_DOC_CLASSIFICATIONS: Classification[] = [
  Classification.ADMIN,
  Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY,
  Classification.ADMIN_FMLA_FORMS,
  Classification.ADMIN_RETURN_TO_WORK,
  Classification.ADMIN_SCHOOL_NOTES,
  Classification.CARE_PLAN,
  Classification.CLEARANCE_REQUEST,
  Classification.DME_REQUEST,
  Classification.FINANCIAL,
  Classification.IDENTIFICATION_CARD,
  Classification.IMAGING_RESULTS,
  Classification.IMAGING_RESULTS_BARIUM_SWALLOW,
  Classification.IMAGING_RESULTS_EKG,
  Classification.IMAGING_RESULTS_ENG,
  Classification.IMAGING_RESULTS_ESOPHAGRAM,
  Classification.IMAGING_RESULTS_MRI,
  Classification.IMAGING_RESULTS_CARDIAC,
  Classification.IMAGING_RESULTS_ULTRASOUND,
  Classification.IMAGING_RESULTS_X_RAY,
  Classification.INCOMPLETE,
  Classification.INSURANCE_CARD,
  Classification.LAB_RESULTS,
  Classification.LAB_RESULTS_PATHOLOGY,
  Classification.LEGAL,
  Classification.LEGAL_POWER_OF_ATTORNEY,
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.NOTIFICATION_APPOINTMENT_CANCELLED_MISSED,
  Classification.NOTIFICATION_APPOINTMENT,
  Classification.PHARMACY_RELATED_REFILL,
  Classification.PRIOR_AUTHORIZATION,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
  Classification.PROCEDURE_REHAB,
  Classification.PROCEDURE_SLEEP,
  Classification.PROGRESS_NOTE,
  Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
  Classification.PROGRESS_NOTE_HOSPITAL_RECORDS,
  Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
  Classification.REFERRAL_REQUEST,
  Classification.UNKNOWN,
];

export function getSientClassificationToExtract(): Classification[] {
  return [
    ...SIENT_DOC_CLASSIFICATIONS,
    Classification.INTAKE,
    Classification.REFERRAL,
    Classification.REFERER_INFORMATION,
    Classification.RESPONSE_TO_VH_REQUEST,
  ];
}

export function getSientClassificationForDocumentUpload(): Classification[] {
  return SIENT_DOC_CLASSIFICATIONS;
}

export async function getSientWorkflows(_group: Group): Promise<Partial<Record<WorkflowType, Classification[]>>> {
  return {
    ...DefaultWorkflow,
    [Workflow.DOC_MANAGEMENT]: getSientClassificationForDocumentUpload(),
  };
}

const SIENT_AUTOCLOSE_CLASSIFICATIONS_HIGH_RATE = new Set<Classification>([
  Classification.CARE_PLAN,
  Classification.IMAGING_RESULTS,
  Classification.IMAGING_RESULTS_ESOPHAGRAM,
  Classification.IMAGING_RESULTS_MRI,
  Classification.IMAGING_RESULTS_ULTRASOUND,
  Classification.LAB_RESULTS,
  Classification.LAB_RESULTS_PATHOLOGY,
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.NOTIFICATION_APPOINTMENT,
  Classification.PHARMACY_RELATED_REFILL,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
  Classification.PROCEDURE_SLEEP,
  Classification.PROGRESS_NOTE,
  Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
]);

const SIENT_AUTOCLOSE_CLASSIFICATIONS_MEDIUM_RATE = new Set<Classification>([
  Classification.ADMIN,
  Classification.IMAGING_RESULTS_ENG,
  Classification.IMAGING_RESULTS_BARIUM_SWALLOW,
  Classification.DME_REQUEST,
]);

export function getSientAutoCloseSamplingRate(section: Section): number {
  const classification = section.uploadClassification ?? section.classification;

  if (SIENT_AUTOCLOSE_CLASSIFICATIONS_HIGH_RATE.has(classification)) return 0.95;
  if (SIENT_AUTOCLOSE_CLASSIFICATIONS_MEDIUM_RATE.has(classification)) return 0.9;

  return 0;
}

const PA_RESPONSE_CLASSIFICATIONS = new Set([
  Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
]);

export function getSientDocumentManagementInstructions(
  baseMapping: DocumentMapping,
  section: Section
): DynamicDocumentInstructions {
  const classification = section.uploadClassification || section.classification;

  if (!PA_RESPONSE_CLASSIFICATIONS.has(classification)) return baseMapping;

  const authorizationStatus = section.reviewedContent?.authorizationStatus;
  const assignedTo = "STAFF, FRONT OFFICE";

  if (authorizationStatus === PriorAuthorizationStatus.DENIED) {
    if (classification === Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE) {
      return {
        ...baseMapping,
        assignedTo,
        folder: "Insurance Denials",
        folderMapping: "Insurance Denials",
        documentLabel: "{{ dateReceived YYYY-MM-DD }}, PRESCRIPTION PRIOR AUTH DENIAL",
      };
    }

    return {
      ...baseMapping,
      assignedTo,
      folder: "Insurance Denials",
      folderMapping: "Insurance Denials",
      documentLabel: "{{ dateReceived YYYY-MM-DD }}, INSURANCE DENIALS - {{ documentType }}",
    };
  }

  if (
    authorizationStatus === PriorAuthorizationStatus.APPROVED ||
    authorizationStatus === PriorAuthorizationStatus.OTHER
  ) {
    if (classification === Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE) {
      return {
        ...baseMapping,
        assignedTo,
        folder: "Insurance Approvals",
        folderMapping: "Insurance Approvals",
        documentLabel: "{{ dateReceived YYYY-MM-DD }}, PRESCRIPTION PRIOR AUTH APPROVAL",
      };
    }

    return {
      ...baseMapping,
      assignedTo,
      folder: "Insurance Approvals",
      folderMapping: "Insurance Approvals",
      documentLabel: "{{ dateReceived YYYY-MM-DD }}, INSURANCE AUTHORIZATIONS - {{ documentType }}",
    };
  }

  return baseMapping;
}

export function getSientDownloadFilename(section: Section | null, group: Group): string | null {
  if (!section) return null;

  const classification = section.uploadClassification || section.classification;

  if (!PA_RESPONSE_CLASSIFICATIONS.has(classification)) return null;

  const mappings = getMappings(group) as { [key: string]: DocumentMapping };
  const baseMapping = mappings[classification as string];

  if (!baseMapping) return null;

  const dynamicMapping = getSientDocumentManagementInstructions(baseMapping, section);
  const documentLabel = renderTemplate(dynamicMapping.documentLabel, section.reviewedContent || "", group);

  if (!documentLabel || documentLabel.includes("{{")) return null;

  return `${documentLabel}.pdf`;
}
